import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/config";
import { UpdatePatientId } from "../../api/Inbound";
import StyledTextField from "../shared/StyledTextField";
import { useNotification } from "../../context/NotificationContext";

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  font-size: 16px;
`;

const Content = styled(DialogContent)`
  background-color: #2b3342;
  color: white;
  padding: 16px;
`;

const StyledTableHeaderCell = styled(TableCell)`
  background-color: #3e526a !important;
  color: white;
`;

const StyledTableCell = styled(TableCell)`
  background-color: #2b3342;
  color: white;
`;

const SubmitModal = ({
  open,
  onClose,
  selectedStudies,
  selectedPatients,
  refreshData,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errorMessages, setErrorMessages] = useState([]);
  const [mismatchList, setMismatchList] = useState([]);
  const [forceMatchQueue, setForceMatchQueue] = useState([]);

  const [forceModalOpen, setForceModalOpen] = useState(false);
  const [mismatchSelections, setMismatchSelections] = useState({});
  const [mismatchSearchQueries, setMismatchSearchQueries] = useState({});
  const { showNotification } = useNotification();

  const normalizeName = (name) => {
    return name.replace(/\^+/g, " ").trim().toLowerCase();
  };

  const handleCloseWithTimeout = () => {
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleUpdatePatientId = async () => {
    if (selectedStudies.length === 0 || selectedPatients.length === 0) {
      console.error("Error: No study or patient selected");
      return;
    }
    setLoading(true);
    setProgress({ current: 0, total: selectedStudies.length });
    setErrorMessages([]);
    setForceMatchQueue([]);
    setMismatchList([]);
    setMismatchSelections({});
    setMismatchSearchQueries({});

    const mismatches = [];
    const seenNames = new Map();

    for (let i = 0; i < selectedStudies.length; i++) {
      const study = selectedStudies[i];
      const patient = selectedPatients[0];
      const normalizedName = normalizeName(study.patientName);

      if (seenNames.has(normalizedName)) {
        console.log(`Skipping duplicate name: ${study.patientName}`);
        continue;
      }

      try {
        const response = await api.post(
          `${UpdatePatientId}/${study.id}/${patient.uuid}`
        );
        console.log(`Study ${i + 1}/${selectedStudies.length} updated`);
        showNotification("Study updated successfully", "success");
        handleCloseWithTimeout();
      } catch (error) {
        if (error.response && error.response.status === 460) {
          console.warn(`Study ${study.id} - Warning: Possible mismatch.`);
          mismatches.push({
            study,
            errorData: error.response.data,
          });
          seenNames.set(normalizedName, error.response.data);
          showNotification("Possible matches found", "warning");
        } else {
          console.error(`Study ${study.id} failed`);
          setErrorMessages((prev) => [
            ...prev,
            `Failed to update Study: ${study.patientName}`,
          ]);
          showNotification("Error updating study", "error");
        }
      }
      setProgress({ current: i + 1, total: selectedStudies.length });
    }
    setLoading(false);
    if (mismatches.length > 0) {
      setMismatchList(mismatches);
      setForceModalOpen(true);
    } else {
      refreshData && refreshData();
    }
  };

  const handleForceModalMatchAll = async () => {
    setLoading(true);

    const allForcePromises = mismatchList.flatMap((mismatch) => {
      const normName = normalizeName(mismatch.study.patientName);
      const studiesToForce = selectedStudies.filter(
        (study) => normalizeName(study.patientName) === normName
      );
      const chosenPatientId =
        mismatchSelections[mismatch.study.id] || selectedPatients[0].uuid;

      return studiesToForce.map((study) =>
        api
          .post(
            `${UpdatePatientId}/${study.id}/${chosenPatientId}?force_match=true`
          )
          .then((response) => {
            if (response.status === 200) {
              console.log(`Forced match for study ${study.id} successful.`);
              return {
                success: true,
                studyId: study.id,
                studyName: study.patientName,
              };
            } else {
              console.error(`Forced match for study ${study.id} failed.`);
              return {
                success: false,
                studyId: study.id,
                studyName: study.patientName,
              };
            }
          })
          .catch((error) => {
            console.error(`Error forcing match for study ${study.id}:`, error);
            return {
              success: false,
              studyId: study.id,
              studyName: study.patientName,
              error,
            };
          })
      );
    });

    const results = await Promise.allSettled(allForcePromises);
    const responses = results.map((result) =>
      result.status === "fulfilled" ? result.value : { success: false }
    );
    const failedCount = responses.filter((res) => !res.success).length;

    if (failedCount > 0) {
      showNotification(`${failedCount} of the studies failed`, "error");
    } else {
      showNotification("All studies were updated successfully", "success");
      handleCloseWithTimeout();
    }

    setLoading(false);
    setTimeout(() => {
      setForceModalOpen(false);
      refreshData && refreshData();
    }, 3000);
  };

  const handleMismatchRowClick = (studyId, patientId) => {
    setMismatchSelections((prev) => ({
      ...prev,
      [studyId]: patientId,
    }));
  };

  const handleModalCancel = () => {
    setForceModalOpen(false);
    setMismatchList([]);
    setMismatchSelections({});
    setMismatchSearchQueries({});
  };

  const handleSearchChange = (studyId, event) => {
    const value = event.target.value;
    setMismatchSearchQueries((prev) => ({
      ...prev,
      [studyId]: value,
    }));
  };

  const formatDate = (yyyymmdd) => {
    if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd;
    const year = yyyymmdd.substring(0, 4);
    const month = yyyymmdd.substring(4, 6);
    const day = yyyymmdd.substring(6, 8);
    return `${month}-${day}-${year}`;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <Header>
          Submit Data
          <IconButton onClick={onClose} style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <Typography variant="body1" gutterBottom>
            Confirm submission of the following studies and patient:
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, color: "#A5E0FF" }}>
            Selected Studies
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>Patient Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Date of Birth</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedStudies.length > 0 ? (
                  selectedStudies.map((study, index) => (
                    <TableRow key={index}>
                      <StyledTableCell>{study.patientName}</StyledTableCell>
                      <StyledTableCell>
                        {study.patientDOB || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {study.patientGender || "N/A"}
                      </StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={3} align="center">
                      No study selected
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mt: 3, color: "#A5E0FF" }}>
            Selected Patient
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>First Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Last Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Date of Birth</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPatients.length > 0 ? (
                  <TableRow>
                    <StyledTableCell>
                      {selectedPatients[0].first_name}
                    </StyledTableCell>
                    <StyledTableCell>
                      {selectedPatients[0].last_name}
                    </StyledTableCell>
                    <StyledTableCell>
                      {selectedPatients[0].date_of_birth}
                    </StyledTableCell>
                    <StyledTableCell>
                      {selectedPatients[0].gender}
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={4} align="center">
                      No patient selected
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {errorMessages.length > 0 && (
            <Typography
              sx={{
                mt: 2,
                color: "red",
                fontSize: "14px",
                fontStyle: "italic",
              }}
            >
              {errorMessages.map((msg, index) => (
                <div key={index}>{msg}</div>
              ))}
            </Typography>
          )}
        </Content>
        <DialogActions sx={{ backgroundColor: "#28929C", padding: "12px" }}>
          <Button onClick={onClose} color="primary" variant="contained">
            Close
          </Button>
          <Button
            onClick={handleUpdatePatientId}
            color="secondary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forceModalOpen}
        onClose={handleModalCancel}
        fullWidth
        maxWidth="md"
      >
        <Header>
          Force Patient Match
          <IconButton onClick={handleModalCancel} style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          {mismatchList.map((mismatch, idx) => {
            const searchQuery = mismatchSearchQueries[mismatch.study.id] || "";
            const filteredMatches =
              mismatch.errorData.potential_matches &&
              mismatch.errorData.potential_matches.filter((patient) => {
                const fullName =
                  `${patient.first_name} ${patient.last_name}`.toLowerCase();
                return fullName.includes(searchQuery.toLowerCase());
              });
            return (
              <div
                key={idx}
                style={{
                  marginBottom: "20px",
                  borderBottom: "1px solid #ccc",
                  paddingBottom: "10px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 5 }}
                >
                  STUDY DETAIL - {mismatch.study.patientName}
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableHeaderCell>
                          Patient Name
                        </StyledTableHeaderCell>
                        <StyledTableHeaderCell>
                          Date of Birth
                        </StyledTableHeaderCell>
                        <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                        <StyledTableHeaderCell>Modality</StyledTableHeaderCell>
                        <StyledTableHeaderCell>Instances</StyledTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell>
                          {mismatch.errorData.study_detail.patient_name}
                        </StyledTableCell>
                        <StyledTableCell>
                          {formatDate(
                            mismatch.errorData.study_detail.patient_dob
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {mismatch.errorData.study_detail.patient_gender}
                        </StyledTableCell>
                        <StyledTableCell>
                          {mismatch.errorData.study_detail.modality || "N/A"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {mismatch.errorData.study_detail.num_instances ||
                            "N/A"}
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold" }}
                    style={{ marginTop: "auto", marginBottom: "auto" }}
                  >
                    Potential Matches
                  </Typography>
                  <StyledTextField
                    label="Search by name"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(mismatch.study.id, e)}
                  />
                </div>

                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: 300, marginBottom: 5 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                        <StyledTableHeaderCell>
                          Date of Birth
                        </StyledTableHeaderCell>
                        <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMatches && filteredMatches.length > 0 ? (
                        filteredMatches.map((patient) => (
                          <TableRow
                            key={patient.uuid}
                            onClick={() =>
                              handleMismatchRowClick(
                                mismatch.study.id,
                                patient.uuid
                              )
                            }
                            selected={
                              mismatchSelections[mismatch.study.id] ===
                              patient.uuid
                            }
                            sx={{
                              cursor: "pointer",
                            }}
                          >
                            <StyledTableCell
                              sx={{
                                cursor: "pointer",
                                backgroundColor:
                                  mismatchSelections[mismatch.study.id] ===
                                  patient.uuid
                                    ? "rgba(158, 155, 170, 0.3)"
                                    : "#2b3342",
                              }}
                            >
                              {patient.first_name} {patient.last_name}
                              {mismatchSelections[mismatch.study.id] ===
                              patient.uuid
                                ? " - (Selected)"
                                : ""}
                              {}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{
                                cursor: "pointer",
                                backgroundColor:
                                  mismatchSelections[mismatch.study.id] ===
                                  patient.uuid
                                    ? "rgba(158, 155, 170, 0.3)"
                                    : "#2b3342",
                              }}
                            >
                              {patient.date_of_birth}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{
                                cursor: "pointer",
                                backgroundColor:
                                  mismatchSelections[mismatch.study.id] ===
                                  patient.uuid
                                    ? "rgba(158, 155, 170, 0.3)"
                                    : "#2b3342",
                              }}
                            >
                              {patient.gender}
                            </StyledTableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <StyledTableCell colSpan={3} align="center">
                            No potential matches available
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            );
          })}
          <Typography variant="h6" sx={{ mt: 2, color: "#A5E0FF" }}>
            CURRENTLY SELECTED PATIENT:
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>First Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Last Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Date of Birth</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <StyledTableCell>
                    {selectedPatients[0]?.first_name}
                  </StyledTableCell>
                  <StyledTableCell>
                    {selectedPatients[0]?.last_name}
                  </StyledTableCell>
                  <StyledTableCell>
                    {selectedPatients[0]?.date_of_birth}
                  </StyledTableCell>
                  <StyledTableCell>
                    {selectedPatients[0]?.gender}
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Content>
        <DialogActions sx={{ backgroundColor: "#28929C", padding: "12px" }}>
          <Button
            onClick={handleModalCancel}
            color="primary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleForceModalMatchAll}
            color="secondary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Force Update All"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubmitModal;
